/**
 * PERFECT LOCKS - Guide templates (shared)
 * Used by sections/article-guide.liquid (blog guides) and
 * sections/guide-content.liquid (page guides). PL 2026-10-22.
 *
 * The section root carries data-pl-guide="blog" or data-pl-guide="page".
 * Loaded with defer, so the document is parsed when this runs.
 *
 * - Table of contents: mobile toggle, scroll spy (links are server-rendered)
 * - Sticky mobile TOC + progress bar positioning (--pl-guide-* CSS vars)
 * - FAQ accordion (progressive enhancement; answers stay in the HTML)
 * - Copy link status
 * - Lite YouTube
 * - Product card swatch image fix, badge placement, inline product images
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-pl-guide]');
  if (!root) return;
  var guideType = root.getAttribute('data-pl-guide');
  var content = document.getElementById('guide-content');
  if (!content) return;

  var docEl = document.documentElement;
  var progressBar = document.getElementById('guide__progress-bar') || document.getElementById('guide-progress-bar');
  var progressIndicator = document.getElementById('guide__progress-indicator') || document.getElementById('guide-progress-indicator');
  var header = document.getElementById('guide-header');
  var siteHeader = document.querySelector('.shopify-section--header, #shopify-section-header, header.header');
  var mobileToc = document.getElementById('guide-mobile-toc');
  var mobileTocPanel = document.getElementById('guide-mobile-toc-panel');
  var mobileTocToggle = mobileToc ? mobileToc.querySelector('.guide__mobile-toc-toggle') : null;
  var tocList = document.getElementById('toc-list');
  var desktopMq = window.matchMedia('(min-width: 1000px)');

  /* ------------------------------------------------------------------
     Offsets: sticky TOC sits under the site header (and the progress bar
     when it is showing); headings scroll into view below both.
     ------------------------------------------------------------------ */
  function headerBottom() {
    if (!siteHeader) return 0;
    return Math.max(0, siteHeader.getBoundingClientRect().bottom);
  }

  function updateOffsets() {
    var top = headerBottom();
    if (progressBar) {
      progressBar.style.top = top + 'px';
      if (progressBar.classList.contains('is-visible')) top += progressBar.offsetHeight;
    }
    docEl.style.setProperty('--pl-guide-sticky-top', top + 'px');
    var tocHeight = (!desktopMq.matches && mobileToc) ? mobileToc.offsetHeight : 0;
    docEl.style.setProperty('--pl-guide-scroll-offset', (top + tocHeight + 12) + 'px');
  }

  /* ------------------------------------------------------------------
     Table of contents
     ------------------------------------------------------------------ */
  function setMobileToc(open) {
    if (!mobileTocToggle || !mobileTocPanel) return;
    mobileTocToggle.setAttribute('aria-expanded', String(open));
    mobileTocPanel.hidden = !open;
  }

  function jumpOffset() {
    var off = headerBottom() + (progressBar ? progressBar.offsetHeight : 0);
    if (!desktopMq.matches && mobileToc) off += mobileToc.offsetHeight;
    return off + 12;
  }

  function jumpTo(id, push) {
    var h = document.getElementById(id);
    if (!h) return;
    function targetY() { return h.getBoundingClientRect().top + window.pageYOffset - jumpOffset(); }
    window.scrollTo({ top: targetY(), behavior: 'instant' });
    setTimeout(function () {
      var y = targetY();
      if (Math.abs(y - window.pageYOffset) > 4) window.scrollTo({ top: y, behavior: 'instant' });
    }, 350);
    if (push && history.pushState) history.pushState(null, '', '#' + id);
    // Move focus to the section for keyboard and screen reader users
    if (!h.hasAttribute('tabindex')) h.setAttribute('tabindex', '-1');
    h.focus({ preventScroll: true });
  }

  function initToc() {
    if (mobileTocToggle && mobileTocPanel) {
      mobileTocToggle.addEventListener('click', function () {
        setMobileToc(mobileTocToggle.getAttribute('aria-expanded') !== 'true');
      });
      mobileTocPanel.addEventListener('click', function (e) {
        if (e.target.closest('a')) setMobileToc(false);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && mobileTocToggle.getAttribute('aria-expanded') === 'true') {
          setMobileToc(false);
          mobileTocToggle.focus();
        }
      });
    }

    // TOC links jump straight to the heading with the final offset (header +
    // progress bar + sticky mobile TOC). Smooth scrolling let the target move
    // mid-scroll (progress bar appearing, images loading), so headings could
    // end up under the sticky bar. One re-check after late layout shifts.
    [tocList, mobileTocPanel].filter(Boolean).forEach(function (list) {
      list.addEventListener('click', function (e) {
        var a = e.target.closest('a[href^="#"]');
        if (!a) return;
        var id = decodeURIComponent(a.getAttribute('href').slice(1));
        if (!document.getElementById(id)) return;
        e.preventDefault();
        setMobileToc(false);
        jumpTo(id, true);
      });
    });
    if (location.hash) {
      var hashId = decodeURIComponent(location.hash.slice(1));
      var hashTarget = document.getElementById(hashId);
      if (hashTarget && hashTarget.tagName === 'H2' && content.contains(hashTarget)) {
        window.addEventListener('load', function () { jumpTo(hashId, false); });
      }
    }

    var headingSelector = guideType === 'page' ? '.rte h2[id]' : 'h2[id]';
    var headings = content.querySelectorAll(headingSelector);
    if (!headings.length || !('IntersectionObserver' in window)) return;

    var lists = [tocList, mobileTocPanel].filter(Boolean);
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var href = '#' + CSS.escape(entry.target.id);
        lists.forEach(function (list) {
          var link = list.querySelector('a[href="' + href + '"]');
          if (!link) return;
          list.querySelectorAll('a').forEach(function (a) {
            a.classList.remove('is-active');
            a.removeAttribute('aria-current');
          });
          link.classList.add('is-active');
          link.setAttribute('aria-current', 'true');
        });
      });
    }, { rootMargin: '0px 0px -70% 0px' });
    headings.forEach(function (h) { observer.observe(h); });
  }

  /* ------------------------------------------------------------------
     Progress bar
     ------------------------------------------------------------------ */
  function initProgress() {
    updateOffsets();
    window.addEventListener('resize', updateOffsets);
    if (desktopMq.addEventListener) desktopMq.addEventListener('change', updateOffsets);

    var ticking = false;
    function update() {
      if (progressBar && header) {
        var docHeight = docEl.scrollHeight - window.innerHeight;
        var pct = docHeight > 0 ? Math.min(100, Math.max(0, (window.scrollY / docHeight) * 100)) : 0;
        progressBar.classList.toggle('is-visible', header.getBoundingClientRect().bottom < 0);
        if (progressIndicator) progressIndicator.style.width = pct + '%';
      }
      updateOffsets();
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------
     FAQ accordion (WAI-ARIA disclosure pattern)
     Questions stay real H3s with a button inside; answers stay in the
     DOM (hidden="until-found" lets browser find-in-page open them).
     Structures handled inside .guide-faq:
       .guide-faq-item > h3 + answer
       bare h3 + following siblings up to the next h3
     ------------------------------------------------------------------ */
  var faqCount = 0;

  function buildFaqItem(h3, answerNodes, wrapper) {
    if (!answerNodes.length) return;
    faqCount += 1;
    var qId = 'guide-faq-q-' + faqCount;
    var aId = 'guide-faq-a-' + faqCount;

    var item = wrapper || document.createElement('div');
    item.classList.add('guide-faq-item', 'guide-faq-item--accordion');
    if (!wrapper) h3.parentNode.insertBefore(item, h3);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'guide-faq__toggle';
    btn.id = qId;
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', aId);
    while (h3.firstChild) btn.appendChild(h3.firstChild);
    var icon = document.createElement('span');
    icon.className = 'guide-faq__icon';
    icon.setAttribute('aria-hidden', 'true');
    btn.appendChild(icon);
    h3.appendChild(btn);
    h3.classList.add('guide-faq__question');
    item.appendChild(h3);

    var panel = document.createElement('div');
    panel.className = 'guide-faq__answer';
    panel.id = aId;
    answerNodes.forEach(function (n) { panel.appendChild(n); });
    panel.setAttribute('hidden', 'until-found');
    item.appendChild(panel);

    function setOpen(open) {
      btn.setAttribute('aria-expanded', String(open));
      if (open) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', 'until-found');
    }
    btn.addEventListener('click', function () {
      setOpen(btn.getAttribute('aria-expanded') !== 'true');
    });
    panel.addEventListener('beforematch', function () { setOpen(true); });
    panel._plOpen = setOpen;
  }

  function initFaq() {
    content.querySelectorAll('.guide-faq').forEach(function (faq) {
      // 1. Card structure
      faq.querySelectorAll('.guide-faq-item').forEach(function (card) {
        var h3 = card.querySelector('h3');
        if (!h3 || h3.closest('.guide-faq-item') !== card) return;
        var nodes = [];
        var n = h3.nextSibling;
        while (n) { var next = n.nextSibling; nodes.push(n); n = next; }
        var real = nodes.filter(function (x) { return x.nodeType === 1 || (x.nodeType === 3 && x.textContent.trim()); });
        if (!real.length) return;
        // Keep the card element as the wrapper; move the h3 to the top.
        card.insertBefore(h3, card.firstChild);
        buildFaqItem(h3, nodes, card);
      });

      // 2. Bare h3 + siblings
      Array.prototype.slice.call(faq.children).forEach(function (child) {
        if (child.tagName !== 'H3' || child.classList.contains('guide-faq__question')) return;
        var nodes = [];
        var n = child.nextSibling;
        while (n && !(n.nodeType === 1 && (n.tagName === 'H3' || n.tagName === 'H2' || n.classList.contains('guide-faq-item')))) {
          var next = n.nextSibling;
          nodes.push(n);
          n = next;
        }
        var real = nodes.filter(function (x) { return x.nodeType === 1 || (x.nodeType === 3 && x.textContent.trim()); });
        if (!real.length) return;
        buildFaqItem(child, nodes, null);
      });
    });

    // Open the answer that holds a #hash target
    function openForHash() {
      if (!location.hash) return;
      var target;
      try { target = content.querySelector(location.hash); } catch (e) { return; }
      var panel = target && target.closest('.guide-faq__answer');
      if (panel && panel._plOpen) panel._plOpen(true);
    }
    openForHash();
    window.addEventListener('hashchange', openForHash);
  }

  /* ------------------------------------------------------------------
     Copy link (blog guides)
     ------------------------------------------------------------------ */
  function initCopyLink() {
    var btn = root.querySelector('[data-copy-url]');
    if (!btn || !navigator.clipboard) return;
    var status = root.querySelector('[data-copy-status]');
    btn.addEventListener('click', function () {
      navigator.clipboard.writeText(btn.getAttribute('data-copy-url')).then(function () {
        btn.setAttribute('aria-label', 'Link copied');
        if (status) status.textContent = 'Link copied';
        setTimeout(function () {
          btn.setAttribute('aria-label', 'Copy link');
          if (status) status.textContent = '';
        }, 2000);
      });
    });
  }

  /* ------------------------------------------------------------------
     Lite YouTube. The inner .lty-playbtn button is the only control
     (no role=button/tabindex on the host, which nested two controls).
     ------------------------------------------------------------------ */
  function initLiteYouTube() {
    document.querySelectorAll('lite-youtube:not([data-initialized])').forEach(function (el) {
      el.setAttribute('data-initialized', 'true');
      var id = el.getAttribute('videoid');
      if (!id) return;

      if (!el.style.backgroundImage || el.style.backgroundImage === 'none') {
        el.style.backgroundImage = 'url("https://i.ytimg.com/vi/' + id + '/hqdefault.jpg")';
      }
      var hq = new Image();
      hq.onload = function () {
        if (hq.width > 120) el.style.backgroundImage = 'url("https://i.ytimg.com/vi/' + id + '/maxresdefault.jpg")';
      };
      hq.src = 'https://i.ytimg.com/vi/' + id + '/maxresdefault.jpg';

      if (!el.querySelector('.lty-playbtn')) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'lty-playbtn';
        b.setAttribute('aria-label', 'Play video');
        b.innerHTML = '<span class="visually-hidden">Play</span>';
        el.appendChild(b);
      }

      el.addEventListener('click', function (e) {
        e.preventDefault();
        if (el.classList.contains('lyt-activated')) return;
        el.classList.add('lyt-activated');
        var iframe = document.createElement('iframe');
        iframe.width = 560;
        iframe.height = 315;
        iframe.title = el.getAttribute('data-title') || 'YouTube video';
        iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&modestbranding=1';
        el.appendChild(iframe);
        iframe.focus();
      });
    });
  }

  if (!window.customElements.get('lite-youtube')) {
    window.customElements.define('lite-youtube', class extends HTMLElement {
      connectedCallback() { setTimeout(initLiteYouTube, 0); }
    });
  }

  /* ------------------------------------------------------------------
     Product cards in guide grids: show the image for the checked swatch.
     Lighter than before: one delegated change listener, a few timed
     re-checks to beat theme JS, and per-card observers that are only
     kept after the shopper interacts (otherwise dropped after 3s).
     ------------------------------------------------------------------ */
  // Page guides also fixed single inline product cards; blog guides did not.
  var GRID_SELECTOR = '.guide__products-grid, .guide__collection-grid, .guide__inline-products-grid' + (guideType === 'page' ? ', .guide__inline-product' : '');
  var SHOW = 'display:block !important; opacity:1 !important; visibility:visible !important; position:absolute !important; top:0; left:0; width:100%; height:100%; object-fit:contain; z-index:1;';

  function cardParts(item) {
    if (window.plCardImgs) window.plCardImgs(item); /* PL 2026-11-03: fill deferred shade images before caching them */
    var wrap = item.querySelector('.product-item__image-wrapper');
    if (!wrap || !item.querySelector('.color-swatch__radio')) return null;
    var primary = wrap.querySelectorAll('.product-item__primary-image');
    if (!primary.length) return null;
    return { all: wrap.querySelectorAll('.product-item__primary-image, .product-item__secondary-image'), primary: primary };
  }

  function showCorrectImage(item) {
    var p = item._plParts;
    if (!p) return;
    item._plUpdating = true;
    var checked = item.querySelector('.color-swatch__radio:checked');
    var mediaId = checked ? checked.getAttribute('data-variant-featured-media') : null;
    var match = null;
    if (mediaId) {
      p.primary.forEach(function (img) { if (!match && img.getAttribute('data-media-id') === mediaId) match = img; });
    }
    if (!match) match = p.primary[0];
    p.all.forEach(function (img) {
      if (img === match) {
        img.removeAttribute('hidden');
        img.style.cssText = SHOW;
      } else {
        img.setAttribute('hidden', '');
        img.style.cssText = 'display:none !important; opacity:0 !important;';
      }
    });
    setTimeout(function () { item._plUpdating = false; }, 50);
  }

  function watch(item, keep) {
    if (!item._plObserver) {
      var pending = null;
      item._plObserver = new MutationObserver(function () {
        if (item._plUpdating) return;
        if (pending) cancelAnimationFrame(pending);
        pending = requestAnimationFrame(function () { showCorrectImage(item); });
      });
      item._plParts.all.forEach(function (img) {
        item._plObserver.observe(img, { attributes: true, attributeFilter: ['hidden', 'style', 'src', 'srcset'] });
      });
    }
    if (keep) item._plKeep = true;
  }

  function initSwatches() {
    var items = [];
    document.querySelectorAll(GRID_SELECTOR).forEach(function (grid) {
      grid.querySelectorAll('.product-item').forEach(function (item) {
        if (item._plParts !== undefined) return;
        item._plParts = cardParts(item);
        if (item._plParts) items.push(item);
      });
    });
    if (!items.length) return;

    items.forEach(function (item) { showCorrectImage(item); watch(item, false); });
    setTimeout(function () {
      items.forEach(function (item) {
        if (!item._plKeep && item._plObserver) { item._plObserver.disconnect(); item._plObserver = null; }
      });
    }, 3000);
    window.addEventListener('load', function () { items.forEach(showCorrectImage); });

    document.addEventListener('change', function (e) {
      if (!e.target.matches || !e.target.matches('.color-swatch__radio')) return;
      var item = e.target.closest('.product-item');
      if (!item || !item._plParts) return;
      watch(item, true);
      showCorrectImage(item);
      requestAnimationFrame(function () { showCorrectImage(item); });
      setTimeout(function () { showCorrectImage(item); }, 150);
      setTimeout(function () { showCorrectImage(item); }, 500);
    });
  }

  /* Page guides: badges inside the image wrapper, inline product image sizing */
  function moveBadges() {
    document.querySelectorAll(GRID_SELECTOR).forEach(function (grid) {
      grid.querySelectorAll('.product-item').forEach(function (item) {
        var wrap = item.querySelector('.product-item__image-wrapper');
        if (!wrap) return;
        item.querySelectorAll('.product-item__label-list, .pl-badge-list, .pl-badge--image-percent, .pl-badge--image-banner').forEach(function (badge) {
          if (wrap.contains(badge) || badge._plMoved) return;
          badge._plMoved = true;
          wrap.appendChild(badge.cloneNode(true));
          badge.style.display = 'none';
        });
      });
    });
  }

  function fixInlineProductImages() {
    document.querySelectorAll('.guide__inline-product').forEach(function (wrapper) {
      wrapper.querySelectorAll('.product-item__image-wrapper').forEach(function (w) {
        w.style.cssText = 'padding-bottom:0 !important; aspect-ratio:unset !important; height:auto !important; position:relative !important; min-height:120px;';
      });
      wrapper.querySelectorAll('.product-item__primary-image, .product-item__secondary-image').forEach(function (img) {
        img.style.cssText = 'position:relative !important; width:100% !important; height:auto !important; max-height:220px; object-fit:contain !important; object-position:center !important;';
        img.removeAttribute('hidden');
      });
    });
  }

  /* ------------------------------------------------------------------ */
  initToc();
  initProgress();
  initFaq();
  initCopyLink();
  initLiteYouTube();
  if (guideType === 'page') {
    // Before initSwatches so its load handler runs first and the swatch
    // image choice is applied last (same final state as the old timers).
    moveBadges();
    fixInlineProductImages();
    setTimeout(moveBadges, 500);
    window.addEventListener('load', function () { moveBadges(); fixInlineProductImages(); });
  }
  initSwatches();
})();
